import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:3005') + '/api';

interface AggRow {
  customers: number;
  day0_revenue: number;
  rev_30d: number;
  rev_60d: number;
  rev_90d: number;
  lifetime_rev: number;
  ltv_30d: number;
  ltv_60d: number;
  ltv_90d: number;
  repeat_pct: number;
  reoptin_pct: number;
  avg_days_to_second: number | null;
  ortto_matched: number;
  avg_engagement: number | null;
  subscribed_pct: number | null;
  active_90d_pct: number | null;
}
interface CategoryRow extends AggRow { category: string }
interface ProductRow extends AggRow { category: string; product_name: string }
interface MonthRow extends AggRow { cohort_month: string }

const fmt = (v: number) => '$' + Math.round(Math.abs(Number(v) || 0)).toLocaleString();
const monthLabel = (d: string) => new Date(d + 'T00:00:00').toLocaleString('en', { month: 'short', year: '2-digit' });

const CAT_COLORS: Record<string, string> = {
  'Custom Clubs & Trade-Ins': 'bg-amber-100 text-amber-800',
  'Schools & Camps': 'bg-blue-100 text-blue-800',
  'SP30': 'bg-violet-100 text-violet-800',
  'Membership & Programs': 'bg-emerald-100 text-emerald-800',
  'Training Aids & Other': 'bg-gray-100 text-gray-600',
};

export default function FirstProductLTV() {
  const [since, setSince] = useState('2024-07-01');
  const [until, setUntil] = useState('');
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [months, setMonths] = useState<MonthRow[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/financial-reports/first-product-cohorts?since=${since}${until ? `&until=${until}` : ''}`);
      const j = await r.json();
      if (!j.success) throw new Error(j.error);
      setCategories(j.data.categories || []);
      setProducts(j.data.products || []);
      setMonths(j.data.months || []);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [since, until]);

  useEffect(() => { load(); }, [load]);

  const toggle = (cat: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  const tot = categories.reduce((a, c) => ({
    customers: a.customers + c.customers,
    d30: a.d30 + c.rev_30d,
    d60: a.d60 + c.rev_60d,
    d90: a.d90 + c.rev_90d,
    lifetime: a.lifetime + c.lifetime_rev,
  }), { customers: 0, d30: 0, d60: 0, d90: 0, lifetime: 0 });

  const metricCells = (r: AggRow) => (
    <>
      <td className="px-3 py-2 text-right font-semibold">{r.customers.toLocaleString()}</td>
      <td className="px-3 py-2 text-right text-gray-500">{r.customers ? fmt(r.day0_revenue / r.customers) : '--'}</td>
      <td className="px-3 py-2 text-right font-semibold text-blue-700">{fmt(r.ltv_30d)}</td>
      <td className="px-3 py-2 text-right font-semibold text-blue-700">{fmt(r.ltv_60d)}</td>
      <td className="px-3 py-2 text-right font-semibold text-blue-700">{fmt(r.ltv_90d)}</td>
      <td className="px-3 py-2 text-right">{r.customers ? fmt(r.lifetime_rev / r.customers) : '--'}</td>
      <td className="px-3 py-2 text-right">{r.repeat_pct}%</td>
      <td className="px-3 py-2 text-right text-gray-500">{r.avg_days_to_second ?? '--'}</td>
      <td className="px-3 py-2 text-right text-gray-500">{r.reoptin_pct}%</td>
      <td className="px-3 py-2 text-right font-medium text-purple-700">{r.avg_engagement != null ? r.avg_engagement : <span className="text-gray-300">--</span>}</td>
      <td className="px-3 py-2 text-right text-gray-500">{r.subscribed_pct != null ? r.subscribed_pct + '%' : <span className="text-gray-300">--</span>}</td>
      <td className="px-3 py-2 text-right text-gray-500">{r.active_90d_pct != null ? r.active_90d_pct + '%' : <span className="text-gray-300">--</span>}</td>
    </>
  );

  const headers = ['', 'Customers', 'Avg day-0', '30d LTV', '60d LTV', '90d LTV', 'Lifetime LTV', 'Repeat %', 'Days to 2nd', 'Re-optin %', 'Ortto Engmt', 'Subscribed %', 'Active 90d %'];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">First Purchase LTV</h1>
          <p className="text-sm text-gray-500">
            What a new customer becomes worth at 30 / 60 / 90 days, split by what they bought <span className="font-medium">first</span>
            (largest line on their first paid order). Includes purchase engagement and Wicked re-optin engagement.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={since} onChange={e => setSince(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          <span className="text-gray-400">→</span>
          <input type="date" value={until} onChange={e => setUntil(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          <button onClick={load} className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 p-3 rounded">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          ['New customers', tot.customers.toLocaleString()],
          ['Avg 30-day LTV', tot.customers ? fmt(tot.d30 / tot.customers) : '--'],
          ['Avg 60-day LTV', tot.customers ? fmt(tot.d60 / tot.customers) : '--'],
          ['Avg 90-day LTV', tot.customers ? fmt(tot.d90 / tot.customers) : '--'],
          ['Avg lifetime', tot.customers ? fmt(tot.lifetime / tot.customers) : '--'],
        ].map(([label, v]) => (
          <div key={label as string} className="bg-white rounded-lg shadow p-4">
            <div className="text-xs uppercase text-gray-500">{label}</div>
            <div className="text-xl font-bold text-gray-900">{v}</div>
          </div>
        ))}
      </div>

      {/* Category table with product drill-down */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {headers.map((h, i) => (
                <th key={i} className={`px-3 py-2 text-xs font-medium text-gray-500 uppercase whitespace-nowrap ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {categories.map(c => {
              const isOpen = expanded.has(c.category);
              const catProducts = products.filter(p => p.category === c.category);
              return [
                <tr key={c.category} onClick={() => toggle(c.category)} className="cursor-pointer hover:bg-blue-50">
                  <td className="px-3 py-2 font-medium whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5">
                      {isOpen ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                      <span className={`px-1.5 py-0.5 rounded text-[11px] font-semibold ${CAT_COLORS[c.category] || 'bg-gray-100 text-gray-600'}`}>{c.category}</span>
                    </span>
                  </td>
                  {metricCells(c)}
                </tr>,
                ...(isOpen ? catProducts.map(p => (
                  <tr key={`${c.category}-${p.product_name}`} className="bg-gray-50/50 text-gray-600">
                    <td className="px-3 py-1.5 pl-10 max-w-[340px] truncate" title={p.product_name}>{p.product_name}</td>
                    {metricCells(p)}
                  </tr>
                )) : []),
              ];
            })}
          </tbody>
        </table>
        {!loading && categories.length === 0 && <p className="text-sm text-gray-400 py-8 text-center">No data for this range.</p>}
      </div>

      {/* Monthly trend */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <div className="px-4 pt-4">
          <h2 className="text-lg font-bold text-gray-900">Monthly trend (all first products)</h2>
        </div>
        <table className="min-w-full divide-y divide-gray-200 text-sm mt-2">
          <thead className="bg-gray-50">
            <tr>
              {['Cohort', 'Customers', 'Avg day-0', '30d LTV', '60d LTV', '90d LTV', 'Lifetime LTV', 'Repeat %', 'Days to 2nd', 'Re-optin %', 'Ortto Engmt', 'Subscribed %', 'Active 90d %'].map((h, i) => (
                <th key={h} className={`px-3 py-2 text-xs font-medium text-gray-500 uppercase whitespace-nowrap ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {months.map(m => {
              const mo = String(m.cohort_month).slice(0, 10);
              const ageDays = Math.floor((Date.now() - new Date(mo + 'T00:00:00').getTime()) / 86_400_000);
              return (
                <tr key={mo} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium whitespace-nowrap">
                    {monthLabel(mo)}
                    {ageDays < 120 && <span className="ml-1.5 text-[10px] uppercase text-amber-600 bg-amber-50 rounded px-1 py-0.5">window open</span>}
                  </td>
                  {metricCells(m)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400">
        First product = the largest line on a customer's first verified paid canonical order (Keap + Shopify; renamed-product duplicate lines removed).
        LTV horizons are cumulative verified collected cash net of refunds from the customer's own first-purchase date (transactions-first, payments-fallback).
        Repeat % = bought a second time (ever). Re-optin % = Wicked recorded a re-optin touch after the first purchase. Ortto Engmt = average Ortto engagement
        score among cohort customers matched in Ortto by email; Subscribed % and Active 90d % (Ortto "last seen" within 90 days) are also of matched customers. Chargebee-native membership signups aren't in the wicked order set, so "Membership & Programs" first purchases are rare by
        construction. Cohorts younger than ~120 days are still maturing. Refreshes hourly.
      </p>
    </div>
  );
}
